import PropTypes from 'prop-types'
import { Stack, Typography, Box, Alert, Divider, Link } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import * as yup from 'yup'

import TextField from 'components/TextField'

import { updateCompanyIntegration } from 'services/requests/companyIntegration'
import { toast } from 'utils'

const initialValues = {
  openPixApiKey: '',
  qCertificaApiKey: '',
  codigoIBGE: ''
}

export default function CompanyIntegrationForm({ defaultValues, onSuccess }) {
  const validationSchema = yup.object({
    openPixApiKey: yup.string(),
    qCertificaApiKey: yup.string(),
    codigoIBGE: yup.string()
  })

  const formik = useFormik({
    initialValues: defaultValues || initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      updateCompanyIntegration({ body: { ...values } })
        .then(() => {
          toast('Integrações atualizadas com sucesso', 'success')
          onSuccess?.()
        })
        .catch((err) => toast(err, 'error'))
        .finally(() => setSubmitting(false))
    },
  })

  return (
    <>
      <Box sx={{ mb: 6 }}>
        <Alert severity="info">
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Como obter os dados para preenchimento:
          </Typography>

          <Typography variant="body2" component="div" sx={{ mt: 2 }}>
            <strong>Chave API OpenPix:</strong> 
            <br /><br />
            1. <strong>Entre no site:</strong> <Link href="https://app.woovi.com/home/applications/tab/list" target="_blank" rel="noopener noreferrer">app.woovi.com</Link> e faça login.
            <br />
            2. Ao entrar, procure por item chamado &quot;API/Plugins&quot; no menu do site.<br />
            3. Clique em &quot;Nova API/Plugin&quot; ou se já aparecer uma chave, apenas <strong>copie a chave</strong>.
            <br />
            4. Cole essa chave no campo <strong>Chave API OpenPix</strong>.
            <br /><br />
            <em>A segunda etapa consiste em configurar Webhooks na <strong>plataforma do OpenPix</strong></em>.
            <br />
            Ainda na seção de API/Plugins, clique em &quot;Novo Webhook&quot; e configure os seguintes URLs:
            <br /><br />
            <ul>
              <li>
                Nome: <strong>Pagamento recebido</strong> (nome sugerido)<br />
                Evento: <strong>OPENPIX:CHARGE_COMPLETED</strong><br />
                URL: <strong style={{ wordBreak: 'break-all' }}>{process.env.REACT_APP_API_URL}/openpix/paid</strong>
              </li>
              <br />
              <li>
                Nome: <strong>Pagamento realizado</strong> (nome sugerido)<br />
                Evento: <strong>OPENPIX:MOVEMENT_CONFIRMED</strong><br />
                URL: <strong style={{ wordBreak: 'break-all' }}>{process.env.REACT_APP_API_URL}/openpix/payment</strong>
              </li>
            </ul>
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" component="div" sx={{ mt: 2 }}>
            <strong>Chave API QCertifica:</strong> Sua chave de API poderá ser recebida atráves do email de cadastro no QCertifica. Ou entre no <Link href="https://portal.qcertifica.com.br/" target="_blank" rel="noopener noreferrer">portal do QCertifica (portal.qcertifica.com.br)</Link> ou em contato com o suporte para obter a chave que será atribuída ao campo <strong>Chave API QCertifica</strong>.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" component="div" sx={{ mt: 2 }}>
            <strong>Código IBGE:</strong> O código IBGE do município pode ser encontrado no <Link href="https://www.ibge.gov.br/explica/codigos-dos-municipios.php" target="_blank" rel="noopener noreferrer">site do IBGE (ibge.gov.br)</Link>. Digite o nome da cidade e copie o código de 7 dígitos correspondente.
          </Typography>
        </Alert>
      </Box>

      <Stack spacing={3}>
        <TextField
          fullWidth
          multiline
          name="openPixApiKey"
          label="Chave API OpenPix"
          value={formik.values.openPixApiKey || ''}
          onChange={formik.handleChange}
          error={formik.touched.openPixApiKey && Boolean(formik.errors.openPixApiKey)}
          helperText={formik.touched.openPixApiKey && formik.errors.openPixApiKey || 'Chave de API do OpenPix para processamento de pagamentos'}
        />

        <TextField
          fullWidth
          multiline
          name="qCertificaApiKey"
          label="Chave API QCertifica"
          value={formik.values.qCertificaApiKey || ''}
          onChange={formik.handleChange}
          error={formik.touched.qCertificaApiKey && Boolean(formik.errors.qCertificaApiKey)}
          helperText={formik.touched.qCertificaApiKey && formik.errors.qCertificaApiKey || 'Chave de API do QCertifica para certificação'}
        />

        <TextField
          fullWidth
          name="codigoIBGE"
          label="Código IBGE"
          value={formik.values.codigoIBGE || ''}
          onChange={formik.handleChange}
          error={formik.touched.codigoIBGE && Boolean(formik.errors.codigoIBGE)}
          helperText={formik.touched.codigoIBGE && formik.errors.codigoIBGE || 'Código IBGE do município'}
        />
      </Stack>

      <Stack direction="column" alignItems="end" justifyContent="end" mt={3}>
        <LoadingButton
          size="large"
          type="submit"
          color="secondary"
          variant="contained"
          onClick={() => formik.handleSubmit()}
          loading={formik.isSubmitting}
        >
          Salvar
        </LoadingButton>
      </Stack>
    </>
  )
}

CompanyIntegrationForm.propTypes = {
  defaultValues: PropTypes.object,
  onSuccess: PropTypes.func
}
